<template>
	<App title="Transaction Details">
		<div v-if="transaction" class="txn-layout">

			<div>
				<a :href="route('transaction.page.group')" class="btn btn-sm btn-outline-secondary">
					<i class="fas fa-arrow-left me-1"></i> Back
				</a>
			</div>

			<!-- ── Hero ──────────────────────────────────────────────────── -->
			<div class="txn-hero">
				<div class="txn-hero-icon">
					<i class="fas fa-exchange-alt"></i>
				</div>
				<div class="txn-hero-info">
					<p class="txn-hero-eyebrow">Transaction #{{ transaction.id }}</p>
					<div class="d-flex align-items-center gap-2 flex-wrap">
						<span :class="['txn-type-badge', `txn-type-badge--${transaction.type}`]">
							{{ transaction.type }}
						</span>
						<span :class="['txn-status-badge', `txn-status-badge--${transaction.status}`]">
							<span class="txn-status-dot"></span>
							{{ transaction.status }}
						</span>
					</div>
				</div>
				<div class="txn-hero-amount">
					<p class="txn-amount-label">Amount</p>
					<p class="txn-amount-value">{{ $render.money(transaction.amount) }}</p>
				</div>
			</div>

			<!-- ── Details grid ───────────────────────────────────────────── -->
			<div class="txn-grid">

				<!-- Agent Information -->
				<div class="txn-section">
					<div class="txn-section-header">
						<i class="fas fa-user me-2 text-muted"></i>
						<span>Agent Information</span>
					</div>
					<div class="txn-section-body">
						<div class="txn-fields">
							<div class="txn-field">
								<span class="txn-field-icon"><i class="fas fa-id-badge"></i></span>
								<div>
									<p class="txn-field-label">Full Name</p>
									<p class="txn-field-value">{{ transaction.agent.fullname }}</p>
								</div>
							</div>
							<div class="txn-field">
								<span class="txn-field-icon"><i class="fas fa-phone"></i></span>
								<div>
									<p class="txn-field-label">Phone Number</p>
									<p class="txn-field-value">{{ transaction.agent.phone }}</p>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Transaction Details -->
				<div class="txn-section">
					<div class="txn-section-header">
						<i class="fas fa-file-invoice me-2 text-muted"></i>
						<span>Transaction Details</span>
					</div>
					<div class="txn-section-body">
						<div class="txn-fields">
							<div class="txn-field">
								<span class="txn-field-icon"><i class="fas fa-hashtag"></i></span>
								<div>
									<p class="txn-field-label">Reference</p>
									<p class="txn-field-value font-monospace">{{ transaction.reference }}</p>
								</div>
							</div>
							<div class="txn-field">
								<span class="txn-field-icon"><i class="fas fa-fingerprint"></i></span>
								<div>
									<p class="txn-field-label">PID</p>
									<p class="txn-field-value font-monospace">{{ transaction.pid }}</p>
								</div>
							</div>
							<div class="txn-field">
								<span class="txn-field-icon"><i class="fas fa-layer-group"></i></span>
								<div>
									<p class="txn-field-label">Platform</p>
									<p class="txn-field-value">{{ transaction.platform }}</p>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Dates -->
				<div class="txn-section txn-section--full">
					<div class="txn-section-header">
						<i class="fas fa-calendar-alt me-2 text-muted"></i>
						<span>Date &amp; Verification</span>
					</div>
					<div class="txn-section-body">
						<div class="txn-fields">
							<div class="txn-field">
								<span class="txn-field-icon"><i class="fas fa-clock"></i></span>
								<div>
									<p class="txn-field-label">Created Date</p>
									<p class="txn-field-value">{{ $render.date(transaction.created_at) }}</p>
								</div>
							</div>
							<div class="txn-field">
								<span class="txn-field-icon"><i :class="['fas', transaction.verified_at ? 'fa-check-circle' : 'fa-hourglass-half']"></i></span>
								<div>
									<p class="txn-field-label">Verified Date</p>
									<p class="txn-field-value">{{ transaction.verified_at ? $render.date(transaction.verified_at) : '—' }}</p>
								</div>
							</div>
						</div>
					</div>
				</div>

			</div>
		</div>
	</App>
</template>
<script lang="ts">
import type Transaction from '@/models/Transaction'
import { error } from '@/ts/app'
import Block from '@/vue/components/Block.vue'
import TextBlock from '@/vue/components/TextBlock.vue'
import Textarea from '@/vue/components/Textarea.vue'
import App from '@/vue/templates/App.vue'
import { AxiosError } from 'axios'
import Swal from 'sweetalert2'
import { Component, Prop, Vue, toNative } from 'vue-facing-decorator'

@Component({
	components: {
		App,
		Block,
		TextBlock,
		Textarea,
	},
})
class Single extends Vue {
	@Prop({ type: Object })
	transaction!: Transaction

	loading = false

	@Prop({ type: String })
	readonly id!: string

	created(){
		console.log('Transaction', this.transaction)
	}

	onVerify(event: Event) {
		event.preventDefault()
		const button = event.target as HTMLButtonElement
		button.disabled = true
		const label = button.innerHTML
		button.innerHTML = '<i class="fas fa-spinner fa-spin"></i>'

		this.$sweet
			.fire({
				title: 'Are You Sure?',
				text: "You won't be able to revert this!",
				icon: 'warning',

				showCancelButton: true,
				confirmButtonText: 'YES',
			})
			.then((result) => {
				if (result.isConfirmed) {
					this.$http
						.put(this.route('transaction.ajax.verify', { transaction: this.transaction.id }))
						.then(() => ((button.innerHTML = 'Done'), this.router.reload({ only: ['transaction'] })))
						.catch((e: AxiosError) => {
							error(e)
							button.innerHTML = label
							// @ts-ignore
							Swal.fire(e.response?.data?.message)
						})
						.finally(() => (button.disabled = false))
				} else {
					button.disabled = false
					button.innerHTML = label
				}
			})
	}
}
export default toNative(Single)
</script>

<style scoped lang="scss">
.txn-layout {
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
}

// ─── Hero ──────────────────────────────────────────────────────────────────
.txn-hero {
	display: flex;
	align-items: center;
	gap: 1.25rem;
	background: #fff;
	border-radius: 12px;
	padding: 1.5rem 1.75rem;
	border: 1px solid #e9ecef;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
	flex-wrap: wrap;
}

.txn-hero-icon {
	flex-shrink: 0;
	width: 56px;
	height: 56px;
	border-radius: 50%;
	background: linear-gradient(135deg, #1e3a2f, #3a7d52);
	color: #fff;
	font-size: 1.2rem;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 12px rgba(30, 58, 47, 0.25);
}

.txn-hero-info {
	flex: 1;
	min-width: 0;
}

.txn-hero-eyebrow {
	font-size: 0.72rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.07em;
	color: #9ca3af;
	margin-bottom: 0.4rem;
}

.txn-hero-amount {
	text-align: right;
	flex-shrink: 0;
}

.txn-amount-label {
	font-size: 0.7rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: #9ca3af;
	margin-bottom: 0.2rem;
}

.txn-amount-value {
	font-size: 1.4rem;
	font-weight: 700;
	color: #111827;
	margin: 0;
}

// ─── Type badge ────────────────────────────────────────────────────────────
.txn-type-badge {
	display: inline-block;
	padding: 0.2rem 0.65rem;
	border-radius: 999px;
	font-size: 0.7rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.06em;

	&--credit  { background: #dcfce7; color: #15803d; }
	&--debit   { background: #fee2e2; color: #dc2626; }
	&--charge  { background: #fef3c7; color: #b45309; }
	&--auto    { background: #dbeafe; color: #1d4ed8; }
	&--penalty { background: #fce7f3; color: #be185d; }
}

// ─── Status badge ──────────────────────────────────────────────────────────
.txn-status-badge {
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
	padding: 0.2rem 0.65rem;
	border-radius: 999px;
	font-size: 0.7rem;
	font-weight: 600;
	text-transform: capitalize;
	border: 1px solid;

	&--pending    { background: #fffbeb; color: #b45309; border-color: #fde68a; }
	&--successful { background: #f0fdf4; color: #15803d; border-color: #bbf7d0; }
	&--failed     { background: #fef2f2; color: #dc2626; border-color: #fecaca; }
}

.txn-status-dot {
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background: currentColor;
	flex-shrink: 0;
}

// ─── Grid ──────────────────────────────────────────────────────────────────
.txn-grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 1.25rem;

	@media (max-width: 640px) {
		grid-template-columns: 1fr;
	}
}

// ─── Sections ──────────────────────────────────────────────────────────────
.txn-section {
	background: #fff;
	border-radius: 12px;
	border: 1px solid #e9ecef;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
	overflow: hidden;

	&--full {
		grid-column: 1 / -1;
	}
}

.txn-section-header {
	display: flex;
	align-items: center;
	padding: 0.75rem 1.25rem;
	background: #f9fafb;
	border-bottom: 1px solid #f1f3f5;
	font-size: 0.82rem;
	font-weight: 600;
	color: #374151;
}

.txn-section-body {
	padding: 1rem 1.25rem;
}

// ─── Fields ────────────────────────────────────────────────────────────────
.txn-fields {
	display: flex;
	flex-direction: column;
	gap: 0.875rem;
}

.txn-field {
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
}

.txn-field-icon {
	flex-shrink: 0;
	width: 32px;
	height: 32px;
	border-radius: 8px;
	background: #f0fdf4;
	color: #16a34a;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 0.78rem;
	margin-top: 0.1rem;
}

.txn-field-label {
	font-size: 0.68rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #9ca3af;
	margin-bottom: 0.15rem;
}

.txn-field-value {
	font-size: 0.9rem;
	font-weight: 500;
	color: #111827;
	margin: 0;
	word-break: break-all;
}
</style>
